/**
 * Interstitial Thunks
 *
 * Async thunks for flight interstitial (fare selection) operations
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { FlightsReviewInterstitialTemplateData } from '@contracts';
import {
  fetchFlightInterstitial as fetchFlightInterstitialApi,
  InterstitialError,
  type InterstitialErrorDetails,
} from '../api/interstitialApi';
import type { RootState, AppDispatch } from '@/store/store';
import type { SelectedFareState } from '../types/flightsTypes';
import { setActiveTemplate, clearPendingTemplate } from '@/features/chat/redux';
import { TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';
import { TRACKING_DATA, fireTrackingEvents } from '@shared/analytics';
import { logger } from '@/shared/utils/logger';
import {
  extractSelectedFaresWithCombo,
  buildInterstitialCombinedContextHashKey,
} from '@/features/flights/utils/reviewUtils';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface FetchInterstitialResult {
  templateData: FlightsReviewInterstitialTemplateData;
  selectedFares: SelectedFareState;
  selectedFareCombo: string;
  /** Search parameters (pax count) for the review flow */
  searchParameters?: { numAdults: number; numChildren: number; numInfant: number } | undefined;
}

/**
 * Reject value type for interstitial thunks.
 * Can be a simple string or structured error details for popup display.
 */
export type InterstitialRejectValue = string | InterstitialErrorDetails;

interface FetchInterstitialWithFareParams {
  crId: string;
  clickedContextHashKey: string;
  journeyIndex: number;
  legIndex: number;
  fareId: string;
}

// ============================================
// Thunks
// ============================================

/**
 * Fetch flight interstitial details (initial review from listing page)
 */
export const fetchFlightInterstitialThunk = createAsyncThunk<
  FetchInterstitialResult,
  { contextHashKey: string; crId: string },
  { state: RootState; dispatch: AppDispatch; rejectValue: InterstitialRejectValue }
>(
  'flights/fetchInterstitial',
  async ({ contextHashKey, crId }, { dispatch, rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const userId = getUUID();
      const interstitialTemplate = await fetchFlightInterstitialApi({
        contextHashKey,
        sessionId,
        userId,
        crId,
      });
      const { selectedFares, selectedFareCombo } = extractSelectedFaresWithCombo(
        interstitialTemplate.data,
      );
      // TODO: Replace with backend-driven trackingInfo from API response
      const trackingInfo = TRACKING_DATA[TEMPLATE_TYPES.FLIGHTS_REVIEW_INTERSTITIAL] ?? null;
      dispatch(
        setActiveTemplate({
          template: 'flights-review-interstitial',
          data: interstitialTemplate.data,
          trackingInfo,
        }),
      );
      fireTrackingEvents(trackingInfo);
      return {
        templateData: interstitialTemplate.data,
        selectedFares,
        selectedFareCombo,
        searchParameters: interstitialTemplate.data.requestParameter,
      };
    } catch (error) {
      logger.error('[InterstitialThunk] Failed to fetch flight interstitial:', error);
      // Clear pending template on error so traveller page is shown again (when going back)
      dispatch(clearPendingTemplate());
      // Return structured error details for popup display if available
      if (error instanceof InterstitialError && error.errorDetails) {
        return rejectWithValue(error.errorDetails);
      }
      if (error instanceof InterstitialError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch flight interstitial');
    }
  },
  {
    // Prevent duplicate API calls - condition runs BEFORE pending action is dispatched
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isReviewLoading } = state.flights.templateListing;
      // Return false to cancel the thunk if already loading
      return !isReviewLoading;
    },
  },
);

/**
 * Fetch interstitial with a specific fare selection
 * Called when user clicks on a fare card in the interstitial
 *
 * @param clickedContextHashKey - The contextHashKey of the clicked fare
 * @param journeyIndex - Index of the journey (0, 1, 2, ...)
 * @param legIndex - Index of the leg within the journey (0, 1, 2, ...)
 * @param fareId - The fare ID for loading state tracking
 */
export const fetchInterstitialWithFareThunk = createAsyncThunk<
  FetchInterstitialResult,
  FetchInterstitialWithFareParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: InterstitialRejectValue }
>(
  'flights/fetchInterstitialWithFare',
  async (
    { clickedContextHashKey, journeyIndex, legIndex, fareId, crId },
    { getState, dispatch, rejectWithValue },
  ) => {
    if (!clickedContextHashKey) {
      logger.error('[InterstitialThunk] Missing clickedContextHashKey:', {
        journeyIndex,
        legIndex,
        fareId,
      });
      return rejectWithValue('Missing contextHashKey');
    }

    const state = getState();
    const { selectedFares } = state.flights.templateInterstitial;

    try {
      // Create key in format "{journeyIndex}-{legIndex}"
      const fareKey = `${journeyIndex}-${legIndex}`;

      // Merge clicked fare with existing selections
      const mergedSelectedFares: SelectedFareState = {
        ...selectedFares,
        [fareKey]: clickedContextHashKey,
      };

      // Build combined context hash key from all selected fares
      const combinedContextHashKey = buildInterstitialCombinedContextHashKey(mergedSelectedFares);

      const sessionId = getOrCreateSessionId();
      const userId = getUUID();
      const interstitialTemplate = await fetchFlightInterstitialApi({
        contextHashKey: combinedContextHashKey,
        sessionId,
        userId,
        crId,
      });

      // Extract selected fares and combo from the updated response
      const { selectedFares: newSelectedFares, selectedFareCombo: newSelectedFareCombo } =
        extractSelectedFaresWithCombo(interstitialTemplate.data);

      // TODO: Replace with backend-driven trackingInfo from API response
      const trackingInfo = TRACKING_DATA[TEMPLATE_TYPES.FLIGHTS_REVIEW_INTERSTITIAL] ?? null;
      dispatch(
        setActiveTemplate({
          template: 'flights-review-interstitial',
          data: interstitialTemplate.data,
          trackingInfo,
        }),
      );
      fireTrackingEvents(trackingInfo);

      return {
        templateData: interstitialTemplate.data,
        selectedFares: newSelectedFares,
        selectedFareCombo: newSelectedFareCombo,
      };
    } catch (error) {
      logger.error('[InterstitialThunk] Failed to fetch interstitial:', error);
      // Return structured error details for popup display if available
      if (error instanceof InterstitialError && error.errorDetails) {
        return rejectWithValue(error.errorDetails);
      }
      if (error instanceof InterstitialError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch flight interstitial');
    }
  },
  {
    // Prevent duplicate API calls - condition runs BEFORE pending action is dispatched
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { loadingFareId } = state.flights.templateInterstitial;
      // Return false to cancel the thunk if already loading
      return !loadingFareId;
    },
  },
);

// ============================================
// Backward Compatibility Aliases (Deprecated)
// ============================================

/**
 * @deprecated Use fetchFlightInterstitialThunk instead
 */
export const fetchFlightReviewThunk = fetchFlightInterstitialThunk;

/**
 * @deprecated Use fetchInterstitialWithFareThunk instead
 */
export const fetchReviewWithFareThunk = fetchInterstitialWithFareThunk;
